<script setup lang="ts">
/**
 * @changelog
 *
 *
 * 2026-02-27 (0.1.0) (jhoward)
 *   - Add Login.vue for user login UI using new 'name' credential.
 *
 * 2026-02-28 (0.1.1) (jhoward)
 *   - Change MaterialFormSwitch name from 'remember_me' to 'remember' for correct form submission.
 *
 * 2026-03-02 (0.2.0) (jhoward)
 *   - Add WebAuthn login challenge and verification logic.
 *   - Implement conditional mediation for WebAuthn.
 *   - Refactor login form for improved UX and accessibility.
 *
 * 2026-03-11 (0.3.0) (jhoward)
 *   - Refactor to use new framework-proxy components for MaterialFormContainer and PeppermillViewport.
 *   - Switch to peppermillCustomFetchProxy for login requests.
 *   - Update login flow to handle new redirect logic and two-factor auth.
 *   - Add help section with PeppermillBigLink for username/password recovery.
 *   - Add new copy and improved content blocks for login information.
 *   - BREAKING: Login API contract and UI structure changed; migration required for consumers.
 *
 * 2026-03-17 (0.3.1) (jhoward)
 *   - Update login logic to redirect to verification if neither email nor phone is verified.
 *
 * 2026-03-24 (0.3.2) (jhoward)
 *   - Use getRouteWithFixedUrl for help links.
 *   - Fix form to use :route instead of :action/:method.
 *
 * 2026-03-30 (0.3.3) (jhoward)
 *   - Refactor login form to use submit-response-callback and improved error/snack handling.
 *   - Add autofocus logic for username field.
 *
 * 2026-04-09 (0.3.4) (jhoward)
 *   - Show logo on login page.
 *   - Redirect to 2FA enrollment if no contact method is present.
 *
 * 2026-08-27 (0.3.5) (jhoward)
 *   - Update LoginResponseData.user to accept the minimal pre-2FA user shape and use
 *     has_email/has_sms_number (tolerant of the full User shape too) to decide routing.
 *
 * 2026-08-27 (0.3.6) (jhoward)
 *   - Fix handleLogin() to stop using `||` to set twoFactorAuthRequired, which discarded a
 *     legitimate `false` from a remembered-device login and left the nav stuck in its pre-login
 *     "2FA required" state.
 *
 * 2026-10-10 (0.4.0) (jhoward)
 *   - Finish signing in with a passkey. The conditional-mediation code from 0.2.0 fetched a challenge and opened
 *     the prompt but never sent the assertion anywhere, so choosing a passkey from the autofill did nothing; it
 *     also read PublicKeyCredential unguarded, a ReferenceError on a browser without WebAuthn. It now goes through
 *     signInWithPasskey() against the passwordless routes, starts when the page opens so the autofill is ready
 *     when the field is focused, and there is a "Sign in with a passkey" button for browsers without the autofill.
 *   - Route after sign-in from one place, whichever way the guest signed in.
 *   - Remove the commented-out remains of the first attempt.
 *
 * 2026-10-15 (0.5.0) (jhoward)
 *   - routeAfterSignIn() drops any account data still in the store from an earlier patron when someone else signs
 *     in.
 *   - Return to ?redirect= (a path on this site only) when the server names nowhere, and carry it to the two-factor
 *     step. The proxy and the router guard set it when a session runs out, but nothing read it, so the guest always
 *     landed on Home.
 *
 * 2026-10-23 (0.6.0) (jhoward)
 *   - Offer passkey sign-in only where canUsePasskeysHere() says this browser can use the shared relying party from
 *     this domain.
 *
 * 2026-10-26 (0.7.0) (jhoward)
 *   - The welcome copy names the property this domain belongs to. It named the three Wendover resorts on every domain.
 *
 * 2026-10-27 (0.8.0) (jhoward)
 *   - On the Reno and Western Village domains, explain to guests used to the legacy app's account number and PIN that
 *     this site needs enrollment, a username and password, and a second sign-in step.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import MaterialButton from '@framework/components/material/MaterialButton.vue';
import type { FetchResponseFormSubmission } from '@framework/components/material/MaterialFormContainer.vue';
import MaterialFormSwitch from '@framework/components/material/MaterialFormSwitch.vue';
import MaterialFormText from '@framework/components/material/MaterialFormText.vue';
import PeppermillBigLink from '@framework/components/PeppermillBigLink.vue';
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import MaterialFormContainer from '@/components/framework-proxy/MaterialFormContainer.vue';
import PeppermillViewport from '@/components/framework-proxy/PeppermillViewport.vue';
import type { MinimalUser, User } from '@/defs/user';
import getPropertyName from '@/helpers/getPropertyName';
import getRouteWithFixedUrl from '@/helpers/getRouteWithFixedUrl';
import safeRedirectPath from '@/helpers/safeRedirectPath';
import signInWithPasskey from '@/helpers/signInWithPasskey';
import { canUsePasskeysHere, isConditionalMediationAvailable } from '@/helpers/webAuthnSupport';
import router from '@/router';
import useAppStore from '@/store';
import { login } from '@rss/routes/';
import { username as usernameRoute } from '@rss/routes/login/help';
import { request as passwordRequest } from '@rss/routes/password';

export interface LoginResponseData {
  [key: string]: any;
  redirect?: string;
  user?: User | MinimalUser;
  csrfToken?: string;
  honeypot?: string;
  twoFactorAuthRequired?: boolean;
}

export interface LoginResponse extends FetchResponseFormSubmission {
  data?: LoginResponseData;
}

const appStore = useAppStore();
const route = useRoute();
const username = ref<string>('');
const password = ref<string>('');
const remember = ref<boolean>(false);
const isPasskeyBusy = ref<boolean>(false);
const canUsePasskey = ref<boolean>(false);

/**
 * Where the guest earns rewards, for the property this domain belongs to. Wendover is three resorts, named as the
 * copy always named them; the others are their guest-facing names (getPropertyName). An unknown property reads as
 * Wendover, the same fallback SetDefaultProperty uses.
 */
const propertyVenue = computed<string>(() => {
  const property = appStore.defaultProperty;

  return '2A' === property || '2B' === property
    ? getPropertyName(property)
    : 'the Rainbow, Peppermill and Montego Bay Resorts';
});

/**
 * Reno and Western Village guests had a legacy app that signed them in with their account number and PIN, so their
 * domains explain the username, password and second step this site asks for instead. The server answers an account
 * number and PIN typed here with the same explanation (auth.legacy_account_pin).
 */
const hadLegacyApp = computed<boolean>(() => '2A' === appStore.defaultProperty || '2B' === appStore.defaultProperty);

/**
 * Where to go once the server has signed the guest in, by password or by passkey.
 */
function routeAfterSignIn(data: LoginResponseData): void {
  //Where the SPA was when the session ran out (peppermillCustomFetchProxy and the router guard set it)
  const returnPath = safeRedirectPath(route.query.redirect);

  if (data.user && data.user.id !== appStore.state.user?.id) {
    appStore.resetAccountState();
  }
  appStore.state.user = data.user || appStore.state.user;
  appStore.csrfToken = data.csrfToken || appStore.csrfToken;
  appStore.honeypot = data.honeypot || appStore.honeypot;
  appStore.state.twoFactorAuthRequired =
    typeof data.twoFactorAuthRequired === 'boolean' ? data.twoFactorAuthRequired : appStore.state.twoFactorAuthRequired;

  if (!appStore.state.user?.email_verified_at && !appStore.state.user?.sms_number_verified_at) {
    const hasEmail =
      'has_email' in (appStore.state.user ?? {})
        ? (appStore.state.user as MinimalUser).has_email
        : !!(appStore.state.user as User | null)?.email;
    const hasSmsNumber =
      'has_sms_number' in (appStore.state.user ?? {})
        ? (appStore.state.user as MinimalUser).has_sms_number
        : !!(appStore.state.user as User | null)?.sms_number;
    if (hasEmail || hasSmsNumber) {
      router.push({ name: 'verification.notice', params: { method: hasSmsNumber ? 'sms' : 'email' } });
    } else {
      router.push({ name: 'login.two-factor.enroll' });
    }
  } else if (data.redirect) {
    router.push({ path: data.redirect });
  } else if (data.twoFactorAuthRequired) {
    //Carried on, so the two-factor step can return the guest to where their session ran out
    router.push({ name: 'login.two-factor.auth', query: returnPath ? { redirect: returnPath } : {} });
  } else {
    router.push(returnPath ? { path: returnPath } : { name: 'home' });
  }
}

async function handleLogin(this: HTMLFormElement, response: LoginResponse): Promise<void> {
  response.form.setFormErrors();
  response.setNewSnackForResponse();

  if (response.data) {
    //A password sign-in ends the quiet passkey prompt; the autofill has nothing left to offer.
    passkeyAbort.abort('Signed in with a password');
    routeAfterSignIn(response.data);
  }
}

/*
 * The username field's autofill offers the passkeys this browser holds for the site, and waits quietly until the
 * guest picks one - no prompt opens on its own. It is started once the page is up so the list is ready when the
 * field is focused; the button below is the same ceremony for browsers without the autofill, and for a guest who
 * would rather press something.
 */
const passkeyAbort = new AbortController();

async function offerPasskeyAutofill(): Promise<void> {
  if (!(await isConditionalMediationAvailable())) {
    return;
  }

  const result = await signInWithPasskey({
    mediation: 'conditional',
    remember: remember.value,
    signal: passkeyAbort.signal,
  });
  if (result.aborted) {
    return;
  }
  if (result.success && result.data) {
    appStore.setNewSnack(10000, result.message, 'check');
    routeAfterSignIn(result.data);
  } else if (result.message) {
    appStore.setNewSnack(15000, result.message, 'error');
  }
}

async function signInWithPasskeyButton(): Promise<void> {
  //Only one prompt at a time: the quiet one has to go before the modal one can open.
  passkeyAbort.abort('Guest pressed the passkey button');

  isPasskeyBusy.value = true;
  const result = await signInWithPasskey({ mediation: 'optional', remember: remember.value });
  isPasskeyBusy.value = false;

  if (result.aborted) {
    return;
  }
  if (result.success && result.data) {
    appStore.setNewSnack(10000, result.message, 'check');
    routeAfterSignIn(result.data);
  } else if (result.message) {
    appStore.setNewSnack(15000, result.message, 'error');
  }
}

onUnmounted(() => {
  passkeyAbort.abort('Component unmounted');
});

const autofocus = useTemplateRef('autofocus');
onMounted(async () => {
  if (appStore.state.user) {
    if (appStore.state.twoFactorAuthRequired) {
      router.push({ name: 'login.two-factor.auth' });
    } else {
      router.push('/');
    }
    return;
  }

  autofocus.value?.$el.querySelector('input')?.focus();
  canUsePasskey.value = await canUsePasskeysHere();
  if (canUsePasskey.value) {
    offerPasskeyAutofill();
  }
});
</script>

<template>
  <peppermill-viewport class="login--container">
    <material-form-container hide-steps :route="login()" :submit-response-callback="handleLogin">
      <img
        alt="Online Players Club Logo"
        class="login--logo"
        src="@/assets/logos/logo-passport-rewards-with-title.png"
      />
      <peppermill-content-block>
        <p>
          You can earn exclusive rewards while playing at {{ propertyVenue }}. When you become a member of the Customer
          Rewards Program, you will be recognized and treated with the special attention you deserve.
        </p>
        <p>
          Earn points by using your passport card while playing slots, table games, or betting at our Race and Sports
          Book! The more you play, the more benefits you'll receive.
        </p>
        <p>Haven't enrolled online yet? <router-link :to="{ name: 'enroll' }">Click Here.</router-link></p>
      </peppermill-content-block>
      <peppermill-content-block v-if="hadLegacyApp">
        <p>
          <strong>Used to signing in with your account number and PIN?</strong> The OPC now works differently.
          <router-link :to="{ name: 'enroll' }">Enroll once</router-link> with your account number to create a username
          and password. Then we'll secure your account with a text, email or authenticator app. Try using a Passkey if your device supports it, it'll speed up your sign-in process.
        </p>
        <p>If you already enrolled in the old version of the OPC, your account has been migrated and you can sign in with your new username and password.</p>
      </peppermill-content-block>
      <peppermill-content-block>
        <material-form-text
          ref="autofocus"
          autofocus
          label="Username"
          name="name"
          required
          @update:value="username = $event"
          autocomplete="username webauthn"
        ></material-form-text>
        <material-form-text
          label="Password"
          name="password"
          type="password"
          required
          @update:value="password = $event"
          autocomplete="current-password"
        ></material-form-text>
        <material-form-switch
          label="Remember Me"
          name="remember"
          @update:value="remember = $event"
          value="1"
        ></material-form-switch>

        <material-button label="Login" type="submit" mod="raised"></material-button>

        <p :style="{ opacity: remember ? 1 : 0 }">
          Please ensure you are not enabling the "Remember Me" feature on a public or shared device.
        </p>
      </peppermill-content-block>
    </material-form-container>

    <template v-if="canUsePasskey">
      <h2>Or use a passkey</h2>
      <peppermill-content-block>
        <p>
          If you've added a passkey to your account on this site, sign in with your face, fingerprint or device PIN
          &mdash; no username or password to type.
        </p>
        <material-button
          label="Sign in with a passkey"
          :disabled="isPasskeyBusy"
          @click.prevent="signInWithPasskeyButton"
        ></material-button>
      </peppermill-content-block>
    </template>

    <h2>Need Help?</h2>
    <peppermill-content-block is-big-link-container>
      <peppermill-big-link
        :path="getRouteWithFixedUrl(usernameRoute()).url"
        icon="account_circle"
        title="Retrieve My Username"
      ></peppermill-big-link>
      <peppermill-big-link
        :path="getRouteWithFixedUrl(passwordRequest()).url"
        icon="password"
        title="Reset My Password"
      ></peppermill-big-link>
    </peppermill-content-block>
  </peppermill-viewport>
</template>

<style scoped>
.login--logo {
  max-width: 100%;
}
</style>
